import type { ActionType, PreviewAction } from "../../api/contracts";
import { GATE_REASON_PREFIX, HOLD_REASON_SUFFIX, MANUAL_OFF_REASON, OFF_SHIFT_REASON, MOVED_STAFF_REASON, LOCK_LEFTOVER_REASON, OUR_START_OBSTACLE, EDGE_OVERLAP_REMOVE_REASON } from "../../btb/constants";
import { formatMinutes, formatPct } from "./format";
import { formatDuration } from "../../lib/duration";

/**
 * Owner-readable sentences for the Activity and Preview "Why" columns, built from each row's
 * fields plus the planner's reason text. The text is a closed set of templates written in
 * src/btb (planner.ts, mutate.ts, evaluate.ts, exclusions.ts); src/btb/reason-coverage.test.ts
 * runs the planner and fails if it ever emits a reason this module does not recognise.
 */

export type ReasonVoice = "done" | "would" | "held";
export interface ReasonInput { reason: string; edge: "start" | "end"; gapMinutes: number; utilizationPct: number; voice: ReasonVoice }
export interface ExplainedReason { sentence: string; recognised: boolean }

/** Activity rows: live writes are done, preview-mode rows would, skips held. */
export function voiceOfAction(type: ActionType): ReasonVoice {
  if (type === "skip") return "held";
  return type.startsWith("would_") ? "would" : "done";
}

/** Preview rows describe a future pass, so nothing is done yet. */
export function voiceOfPlanned(type: PreviewAction["type"]): ReasonVoice {
  return type === "skip" ? "held" : "would";
}

type Hold = keyof typeof GATE_REASON_PREFIX | keyof typeof HOLD_REASON_SUFFIX;
type Obstacle = { kind: "appointment" } | { kind: "block"; title: string } | { kind: "ourStart" };
type Core =
  | { t: "create"; blockMinutes: number; nextTo: string | null }
  | { t: "removeBooked"; thresholdPct: number; minGapMinutes: number }
  | { t: "removeOverlap" }
  | { t: "edgeOverlapRemove" }
  | { t: "removeDuplicate" }
  | { t: "resize"; blockMinutes: number; oldMinutes: number; nextTo: string | null }
  | { t: "resizeBlocked"; blockMinutes: number; obstacle: Obstacle }
  | { t: "createBlocked"; obstacle: Obstacle }
  | { t: "release"; title: string }
  | { t: "windDown" }
  | { t: "pastWindow"; days: number }
  | { t: "offShift" }
  | { t: "movedStaff" }
  | { t: "lockLeftover" }
  | { t: "lockedDnb"; hours: string }
  | { t: "excluded"; scope: "this" | "every"; reason: string | null }
  | { t: "excludedRole"; role: string }
  | { t: "refit"; blockMinutes: number; title: string }
  | { t: "refitRemove"; title: string }
  | { t: "bookingRelease"; app: string }
  | { t: "protectedCreate"; blockFrom: string; blockTo: string; from: string; to: string }
  | { t: "protectedResize"; blockMinutes: number; blockFrom: string; blockTo: string; from: string; to: string }
  | { t: "protectedRemove"; blockFrom: string; blockTo: string; from: string; to: string }
  | { t: "dayOff"; weekday: string }
  | { t: "adopt"; title: string; minutes: number }
  | { t: "managerWins"; title: string }
  | { t: "coverageCreate"; from: string; to: string; why: "alone" | "kept" | "fewer" | "last"; names: string }
  | { t: "coverageResize"; from: string; to: string }
  | { t: "coverageRemove"; from: string; to: string }
  | { t: "manualOff" };

/** `lockedSuffix` in src/btb/constants: which edge and how many hours before the shift. */
const LOCKED_SUFFIX = / \(skipped: (start|end) time locked (\d+) hours? before the shift (?:starts|ends)\)$/;
/** The clock-time form rows carried before locks counted back from the shift (until 2026-10-20). */
const LOCKED_CLOCK_SUFFIX = / \(skipped: (start|end) time locked after (\d{1,2}(?:am|pm)) (the night before|the same day)\)$/;

/** "12 hours" / "1 hour". */
function hoursText(hours: string): string {
  return `${hours} hour${hours === "1" ? "" : "s"}`;
}

function lockedText(edge: string, hours: string): string {
  return `The ${edge} time locks ${hoursText(hours)} before the shift ${edge === "start" ? "starts" : "ends"}`;
}

function lockedClockText(edge: string, clock: string, day: string): string {
  return day === "the night before" ? `Tomorrow's ${edge} time is locked from ${clock} the night before` : `This day's ${edge} time is locked from ${clock} that day`;
}

/**
 * `[gate prefix] core [hold suffix]`; a prefix and a suffix never both appear, but each is handled
 * alone. `hold` is the owner sentence for whichever was found.
 */
function split(reason: string): { core: string; hold: string | null } {
  let core = reason;
  let hold: string | null = null;
  for (const [key, prefix] of Object.entries(GATE_REASON_PREFIX)) {
    if (core.startsWith(prefix)) { core = core.slice(prefix.length); hold = HOLD_TEXT[key as Hold]; break; }
  }
  const lock = LOCKED_SUFFIX.exec(core);
  if (lock) return { core: core.slice(0, lock.index), hold: hold ?? lockedText(lock[1], lock[2]) };
  const clockLock = LOCKED_CLOCK_SUFFIX.exec(core);
  if (clockLock) return { core: core.slice(0, clockLock.index), hold: hold ?? lockedClockText(clockLock[1], clockLock[2], clockLock[3]) };
  for (const [key, suffix] of Object.entries(HOLD_REASON_SUFFIX)) {
    if (core.endsWith(suffix)) { core = core.slice(0, -suffix.length); hold ??= HOLD_TEXT[key as Hold]; break; }
  }
  return { core, hold };
}

function obstacle(text: string): Obstacle {
  if (text === OUR_START_OBSTACLE) return { kind: "ourStart" };
  const m = /^block "(.*)"$/s.exec(text);
  return m ? { kind: "block", title: m[1] } : { kind: "appointment" };
}

// Anchored on the fixed words of each template in src/btb; free text (titles, reasons, roles) is
// taken greedily between them. Old rows spell "buffer block"; both spellings are ours.
const OBSTACLE = String.raw`(appointment \S+|block ".*"|the BTB block at the shift start)`;
/** A `clockLabel` in src/btb/protected-hours.ts: "9am", "10:30am", "12pm" or "midnight". */
const TIME = String.raw`(\d{1,2}(?::\d{2})?(?:am|pm)|midnight)`;
const RANGE = `${TIME}–${TIME}`;
const RX = {
  protectedCreate: new RegExp(`^\\d+m open at shift (?:start|end), tier \\d+ → \\d+m block, but ${RANGE} falls inside protected hours ${RANGE}$`),
  protectedResize: new RegExp(`^wanted to resize to (\\d+)m but ${RANGE} falls inside protected hours ${RANGE}$`),
  protectedRemove: new RegExp(`^inside protected hours ${RANGE}: removing the ${RANGE} BTB block$`),
  dayOff: /^BTB is off on (\w+)s here: removing the BTB block$/,
  resizeBlocked: new RegExp(`^wanted to resize to (\\d+)m but ${OBSTACLE} is in the way$`, "s"),
  createBlocked: new RegExp(`^gap \\d+m matches tier \\d+ but ${OBSTACLE} is in the way$`, "s"),
  create: /^(\d+)m open at shift (?:start|end), tier \d+ → (\d+)m block(?: next to "(.*)")?$/s,
  removeBooked: /^utilization \d+(?:\.\d+)?% >= (\d+(?:\.\d+)?)% and gap \d+m < (\d+)m$/,
  removeOverlap: /^appointment \S+ overlaps the block$/,
  removeDuplicate: /^duplicate (?:buffer|BTB) block for the shift (?:start|end)$/,
  resize: /^gap \d+m now matches tier \d+ \((\d+)m\), block is (\d+)m(?: next to "(.*)")?$/s,
  release: /^staff renamed the (?:buffer|BTB) block to "(.*)"; left to them$/s,
  pastWindow: /^past this location's planning window \((\d+) days\): removing this system's BTB blocks, adding none$/,
  windDown: /^location winding down \(set by .*\): removing this system's (?:buffer|BTB) blocks, adding none$/s,
  excluded: /^staff excluded at (this|every) location by [^:]*(?:: (.*))?$/s,
  excludedRole: /^staff excluded automatically: Boulevard role "(.*)"$/s,
  refit: /^turnover block "(.*)" overlaps the BTB block; refit to (\d+)m (?:before|after) it$/s,
  bookingRelease: /^released for a booking via (.*?)(?: \(ref .*\))?$/s,
  refitRemove: /^turnover block "(.*)" overlaps the BTB block; gap \d+m matches no tier$/s,
  /** `adoptReason` in src/btb/constants. */
  adopt: /^took over the manager's BTB block "(.*)" \((\d+)m\); left as set until a booking changes the gap$/s,
  /** `managerWinsReason` (MANAGER_WINS_REASON_PREFIX) in src/btb/constants. */
  managerWins: /^the manager placed a BTB block "(.*)" on this edge; theirs is kept$/s,
  /** `coverageCreateReason` in src/btb/constants (alone / kept / fewer / last). */
  coverageCreate: /^keep someone in: left open (\S+)–(\S+); (?:(nobody else is in then)|(.+) already got this one|(.+) had fewer BTB blocks this week|even count and (.+) didn't get the last one)$/s,
  /** `coverageResizeReason` in src/btb/constants. */
  coverageResize: /^keep someone in: wanted to grow to \d+m but nobody else is in (\S+)–(\S+)$/,
  /** `coverageRemoveReason` in src/btb/constants. */
  coverageRemove: /^keep someone in: block removed so someone is in (\S+)–(\S+)$/,
  /** `lockedDnbReason` in src/btb/constants; the edge comes from the row. */
  lockedDnb: /^locked in as DNB: (?:start|end) time locked (\d+) hours? before the shift (?:starts|ends)$/,
};

function parseCore(text: string): Core | null {
  let m: RegExpExecArray | null;
  // The protected-hours templates end in fixed words, so none of the looser patterns below can claim them.
  if ((m = RX.protectedCreate.exec(text))) return { t: "protectedCreate", blockFrom: m[1], blockTo: m[2], from: m[3], to: m[4] };
  if ((m = RX.protectedResize.exec(text))) return { t: "protectedResize", blockMinutes: Number(m[1]), blockFrom: m[2], blockTo: m[3], from: m[4], to: m[5] };
  if ((m = RX.protectedRemove.exec(text))) return { t: "protectedRemove", from: m[1], to: m[2], blockFrom: m[3], blockTo: m[4] };
  if ((m = RX.dayOff.exec(text))) return { t: "dayOff", weekday: m[1] };
  if ((m = RX.resizeBlocked.exec(text))) return { t: "resizeBlocked", blockMinutes: Number(m[1]), obstacle: obstacle(m[2]) };
  if ((m = RX.createBlocked.exec(text))) return { t: "createBlocked", obstacle: obstacle(m[1]) };
  if ((m = RX.refit.exec(text))) return { t: "refit", title: m[1], blockMinutes: Number(m[2]) };
  if ((m = RX.refitRemove.exec(text))) return { t: "refitRemove", title: m[1] };
  if ((m = RX.create.exec(text))) return { t: "create", blockMinutes: Number(m[2]), nextTo: m[3] ?? null };
  if ((m = RX.removeBooked.exec(text))) return { t: "removeBooked", thresholdPct: Number(m[1]), minGapMinutes: Number(m[2]) };
  if (RX.removeOverlap.test(text)) return { t: "removeOverlap" };
  if (text === EDGE_OVERLAP_REMOVE_REASON) return { t: "edgeOverlapRemove" };
  if (RX.removeDuplicate.test(text)) return { t: "removeDuplicate" };
  if ((m = RX.resize.exec(text))) return { t: "resize", blockMinutes: Number(m[1]), oldMinutes: Number(m[2]), nextTo: m[3] ?? null };
  if ((m = RX.release.exec(text))) return { t: "release", title: m[1] };
  if (RX.windDown.test(text)) return { t: "windDown" };
  if ((m = RX.pastWindow.exec(text))) return { t: "pastWindow", days: Number(m[1]) };
  if (text === OFF_SHIFT_REASON) return { t: "offShift" };
  if (text === MOVED_STAFF_REASON) return { t: "movedStaff" };
  if (text === LOCK_LEFTOVER_REASON) return { t: "lockLeftover" };
  if ((m = RX.adopt.exec(text))) return { t: "adopt", title: m[1], minutes: Number(m[2]) };
  if ((m = RX.managerWins.exec(text))) return { t: "managerWins", title: m[1] };
  if ((m = RX.coverageCreate.exec(text))) {
    const why = m[3] ? "alone" : m[4] ? "kept" : m[5] ? "fewer" : "last";
    return { t: "coverageCreate", from: m[1], to: m[2], why, names: m[4] ?? m[5] ?? m[6] ?? "" };
  }
  if ((m = RX.coverageResize.exec(text))) return { t: "coverageResize", from: m[1], to: m[2] };
  if ((m = RX.coverageRemove.exec(text))) return { t: "coverageRemove", from: m[1], to: m[2] };
  if (text === MANUAL_OFF_REASON) return { t: "manualOff" };
  if ((m = RX.lockedDnb.exec(text))) return { t: "lockedDnb", hours: m[1] };
  if ((m = RX.excluded.exec(text))) return { t: "excluded", scope: m[1] as "this" | "every", reason: m[2] ?? null };
  if ((m = RX.excludedRole.exec(text))) return { t: "excludedRole", role: m[1] };
  if ((m = RX.bookingRelease.exec(text))) return { t: "bookingRelease", app: m[1] };
  return null;
}

function obstacleText(o: Obstacle): string {
  if (o.kind === "ourStart") return "the BTB block at the start of the shift";
  return o.kind === "appointment" ? "a client appointment" : `the block "${o.title}"`;
}

const HOLD_TEXT: Record<Hold, string> = {
  // Matched by LOCKED_CLOCK_SUFFIX before this table is read; kept so every suffix has a sentence.
  lockedStart: lockedClockText("start", "7pm", "the night before"),
  elapsedStart: "The block's start time had already passed",
  elapsedEnd: "The block had already ended",
  disabled: "This location is turned off",
  paused: "This location is paused",
  globallyPaused: "Corporate has paused every location",
  unmanaged: "The block is not one this tool manages",
  bookingHold: "A booking was in progress for this time",
  // Whole sentences: heldSentence returns these two as they are for an adopt row.
  tracked: "Not taken over: this block is already tracked.",
  adoptWaiting: "Not taken over yet: our BTB block on this edge has to come off first.",
  coverageWaiting: "Another BTB block had to come off first and has not yet",
};

/** "2 hr", "1 hr 30 min", "45 min": the length of a block a manager placed. */
function adoptMinutes(min: number): string {
  if (min < 60) return `${min} min`;
  return min % 60 === 0 ? `${min / 60} hr` : `${Math.floor(min / 60)} hr ${min % 60} min`;
}

/** "at the end of the shift", or past the cleaning / setup block that moved the edge. */
function where(edge: "start" | "end", nextTo: string | null): string {
  return nextTo === null ? `at the ${edge} of the shift` : `${edge === "end" ? "before" : "after"} "${nextTo}" at the ${edge} of the shift`;
}

/** "end before" / "start after": which way a re-fitted block now sits against the cleaning / setup block. */
const refitSide = (edge: "start" | "end") => (edge === "end" ? "end before" : "start after");

function coverageCreateSentence(c: Extract<Core, { t: "coverageCreate" }>): string {
  const plural = /, | and /.test(c.names);
  const head = `Left open ${c.from}–${c.to} so someone is in the salon:`;
  switch (c.why) {
    case "alone": return `${head} nobody else is in then.`;
    case "kept": return `${head} ${c.names} already ${plural ? "have" : "has"} this one.`;
    case "fewer": return `${head} ${c.names} ${plural ? "have" : "has"} had fewer BTB blocks this week.`;
    case "last": return `${head} even count, and ${c.names} didn't get the last one.`;
  }
}

/** The core in the done or would voice. Blocked cores are only ever skips and read as held. */
function coreSentence(core: Core, i: ReasonInput): string {
  const would = i.voice === "would";
  const gap = formatMinutes(i.gapMinutes), util = formatPct(i.utilizationPct), edge = i.edge;
  const off = would ? "would come off" : "came off";
  switch (core.t) {
    case "create": return `${gap} open ${where(edge, core.nextTo)}, so a ${formatDuration(core.blockMinutes, "length")} BTB block ${would ? "would be placed" : "was placed"}.`;
    case "removeBooked": return `The day is ${util} booked with ${gap} open next to the block, so it ${off}. The rule is ${core.thresholdPct}% booked and under ${formatMinutes(core.minGapMinutes)}.`;
    case "removeOverlap": return `A client was booked into the block, so it ${off}.`;
    case "edgeOverlapRemove": return `The BTB block at the end of the shift ran into the one at the start, so it ${off}.`;
    case "removeDuplicate": return `Two BTB blocks sat at the ${edge} of the shift, so the extra one ${off}.`;
    case "resize": return `${gap} open ${where(edge, core.nextTo)} now calls for a ${formatDuration(core.blockMinutes, "length")} block, so the ${formatDuration(core.oldMinutes, "length")} block ${would ? "would be resized" : "was resized"}.`;
    case "release": return `A staff member renamed the block to "${core.title}", so ${would ? "it would be left to them" : "it is theirs now and this tool leaves it alone"}.`;
    case "pastWindow": return `This day is further out than this location plans BTB blocks (${core.days} days), so the block ${off}.`;
    case "windDown": return `This location is being wound down, so the block ${off}.`;
    case "offShift": return `No shift covers this block any more (the shift was removed or shortened), so the block ${off}.`;
    case "movedStaff": return `A staff member moved this block onto someone else's calendar, so it ${off}.`;
    case "lockLeftover": return `A leftover BTB block next to the DNB block ${off}.`;
    case "lockedDnb": return would
      ? `The BTB block at the ${edge} of the shift would have been locked in as DNB ${hoursText(core.hours)} before the shift.`
      : `The BTB block at the ${edge} of the shift was locked in as DNB ${hoursText(core.hours)} before the shift. It won't change again.`;
    case "excluded": return `This staff member is excluded at ${core.scope === "this" ? "this location" : "every location"}, so the block ${off}.${core.reason ? ` Reason given: ${core.reason}.` : ""}`;
    case "excludedRole": return `Boulevard lists this staff member as "${core.role}", so the block ${off}.`;
    case "refit": return `The block "${core.title}" sits on the BTB block at the ${edge} of the shift, so the BTB block ${would ? "would be" : "was"} moved to ${refitSide(edge)} it (${formatMinutes(core.blockMinutes)}).`;
    case "refitRemove": return `The block "${core.title}" sits on the BTB block at the ${edge} of the shift and leaves ${gap} open ${edge === "end" ? "before" : "after"} it, too little for a BTB block, so the BTB block ${off}.`;
    case "bookingRelease": return `${core.app} asked for this time to book a client, so the block ${off}.`;
    case "protectedRemove": return `The BTB block at the ${edge} of the shift ${off}: ${core.blockFrom} to ${core.blockTo} falls inside protected hours (${core.from} to ${core.to}).`;
    case "dayOff": return `BTB is off on ${core.weekday}s here, so the BTB block at the ${edge} of the shift ${off}.`;
    case "adopt": return `${would ? "Would take over" : "Took over"} the BTB block "${core.title}" your team placed (${adoptMinutes(core.minutes)}). It stays as set until a booking changes this edge.`;
    case "managerWins": return `${would ? "Would remove" : "Removed"} our BTB block: your team placed "${core.title}" on this edge, so theirs is kept.`;
    case "coverageCreate": return coverageCreateSentence(core);
    case "coverageResize": return `Not made longer: nobody else would be in the salon ${core.from}–${core.to}.`;
    case "coverageRemove": return `Taken off so someone is in the salon ${core.from}–${core.to}.`;
    case "manualOff": return would ? "Would stop managing this hand-placed BTB block; it would stay as it is." : "Stopped managing this hand-placed BTB block; it stays as it is.";
    case "createBlocked": case "resizeBlocked": case "protectedCreate": case "protectedResize": return heldSentence(core, null, i);
  }
}

/** The core as the thing that would have happened, for a held row: "…, so nothing changed. Otherwise {this}." */
function wouldHave(core: Core, i: ReasonInput): string {
  const gap = formatMinutes(i.gapMinutes), util = formatPct(i.utilizationPct), edge = i.edge;
  switch (core.t) {
    case "create": return `a ${formatDuration(core.blockMinutes, "length")} BTB block would have been placed ${where(edge, core.nextTo)}`;
    case "refit": return `the BTB block at the ${edge} of the shift would have been moved to ${refitSide(edge)} "${core.title}" (${formatMinutes(core.blockMinutes)})`;
    case "refitRemove": return `the BTB block at the ${edge} of the shift would have come off, since "${core.title}" sits on it and leaves too little open time`;
    case "removeBooked": return `the block would have come off, since the day is ${util} booked with ${gap} open next to it`;
    case "removeOverlap": return "the block would have come off, since a client was booked into it";
    case "edgeOverlapRemove": return "the block would have come off, since it ran into the BTB block at the start of the shift";
    case "removeDuplicate": return `the extra BTB block at the ${edge} of the shift would have come off`;
    case "resize": return `the ${formatDuration(core.oldMinutes, "length")} block at the ${edge} of the shift would have been resized to ${formatMinutes(core.blockMinutes)}`;
    case "release": return `the block renamed "${core.title}" would have been left to staff`;
    case "pastWindow": return "the block would have come off, since the day is past the planning window";
    case "windDown": return "the block would have come off for the wind-down";
    case "offShift": return "the block would have come off, since no shift covers it any more";
    case "movedStaff": return "the block would have come off, since a staff member moved it onto someone else's calendar";
    case "lockLeftover": return "the leftover BTB block next to the DNB block would have come off";
    case "lockedDnb": return `the BTB block at the ${edge} of the shift would have been locked in as DNB ${hoursText(core.hours)} before the shift`;
    case "excluded": case "excludedRole": return "the block would have come off because this staff member is excluded";
    case "bookingRelease": return `the block would have come off so ${core.app} could book a client`;
    case "createBlocked": return `a BTB block at the ${edge} of the shift would still have had ${obstacleText(core.obstacle)} in the way`;
    case "resizeBlocked": return `the BTB block at the ${edge} of the shift would still have had ${obstacleText(core.obstacle)} in the way`;
    case "protectedCreate": return `a BTB block at the ${edge} of the shift would still have fallen inside protected hours (${core.from} to ${core.to})`;
    case "protectedResize": return `the BTB block at the ${edge} of the shift would still have had ${core.blockFrom} to ${core.blockTo} inside protected hours (${core.from} to ${core.to})`;
    case "protectedRemove": return `the block would have come off, since ${core.blockFrom} to ${core.blockTo} falls inside protected hours (${core.from} to ${core.to})`;
    case "adopt": return `the BTB block "${core.title}" your team placed would have been taken over`;
    case "managerWins": return `our BTB block would have come off, since your team placed "${core.title}" on this edge`;
    case "coverageCreate": return `the block would have been left out so someone is in the salon ${core.from}–${core.to}`;
    case "coverageResize": return `the block would not have been made longer, since nobody else would be in the salon ${core.from}–${core.to}`;
    case "coverageRemove": return `the block would have come off so someone is in the salon ${core.from}–${core.to}`;
    case "manualOff": return "the hand-placed BTB blocks would have been left as they are, no longer managed";
    case "dayOff": return `the block would have come off, since BTB is off on ${core.weekday}s here`;
  }
}

function heldSentence(core: Core, hold: string | null, i: ReasonInput): string {
  // The two adopt-only holds are whole sentences; any other gate on an adopt reads like the rest.
  if (core.t === "adopt" && (hold === HOLD_TEXT.tracked || hold === HOLD_TEXT.adoptWaiting)) return hold;
  // Coverage sentences read the same in every voice.
  if (!hold && (core.t === "coverageCreate" || core.t === "coverageResize" || core.t === "coverageRemove")) return coreSentence(core, i);
  if (hold) return `${hold}, so nothing changed. Otherwise ${wouldHave(core, i)}.`;
  const gap = formatMinutes(i.gapMinutes), edge = i.edge;
  if (core.t === "createBlocked") return `${gap} open at the ${edge} of the shift, but ${obstacleText(core.obstacle)} is in the way of a BTB block, so nothing changed.`;
  if (core.t === "resizeBlocked") return `The BTB block at the ${edge} of the shift should now be ${formatMinutes(core.blockMinutes)}, but ${obstacleText(core.obstacle)} is in the way, so it stayed as it was.`;
  if (core.t === "protectedCreate") return `The BTB block at the ${edge} of the shift was left out: ${core.blockFrom} to ${core.blockTo} falls inside protected hours (${core.from} to ${core.to}).`;
  if (core.t === "protectedResize") return `The BTB block at the ${edge} of the shift was not resized: the new ${formatDuration(core.blockMinutes)} block (${core.blockFrom} to ${core.blockTo}) would fall inside protected hours (${core.from} to ${core.to}).`;
  return `Nothing changed. Otherwise ${wouldHave(core, i)}.`;
}

export function explainReason(i: ReasonInput): ExplainedReason {
  const { core: text, hold } = split(i.reason);
  const core = parseCore(text);
  if (!core) return { sentence: i.reason, recognised: false };
  const held = i.voice === "held" || hold !== null || core.t === "createBlocked" || core.t === "resizeBlocked" || core.t === "protectedCreate" || core.t === "protectedResize";
  return { sentence: held ? heldSentence(core, hold, i) : coreSentence(core, i), recognised: true };
}
